import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { get } from 'lodash';

import { DocumentEnum } from '../../../services/document/documentsConfig';
import useParameterStore from '../../../store/parameterStore/useParameterStore';
import usePinStore from '../../../store/pinStore/usePinStore';
import useSignUpStore from '../../../store/signUpStore/useSignUpStore';
import { UserExistsExceptionCode } from '../../../store/userStore/enums/UserExistsExceptionCode';
import useUserStore from '../../../store/userStore/useUserStore';
import { RouteRedirectConfig } from '../types/RouteRedirectConfig';

export const routeRedirectConfigs: Record<string, RouteRedirectConfig> = {
  /* sign out routes */
  '/unified/sign-out': {
    targetPath: () => '/sign-out',
  },

  /* sign in & activation routes */
  '/unified/sign-in': {
    targetPath: () => '/sign-in',
  },
  '/unified/sign-in/verify/pin': {
    targetPath: ({ locationState }) => {
      const primaryPhoneNumber = get(locationState, 'primaryPhoneNumber');

      if (primaryPhoneNumber) {
        usePinStore.setState({ primaryPhoneNumber });
      }

      return '/sign-in/pin-verify';
    },
  },
  '/unified/sign-in/application-update': {
    targetPath: () => '/sign-in/application-update',
  },
  '/unified/sign-in/activation': {
    targetPath: () => {
      const authType = localStorage.getItem(
        'consumer_portal:authType',
      ) as AuthType;
      const coApplicantCollectAgreeToTerms = localStorage.getItem(
        'consumer_portal:coApplicantCollectAgreeToTerms',
      );
      const collectAgreeToTerms = localStorage.getItem(
        'consumer_portal:collectAgreeToTerms',
      );
      const firstName = localStorage.getItem('consumer_portal:firstName');
      const hasDuplicateApplication = localStorage.getItem(
        'consumer_portal:hasDuplicateApplication',
      );
      const leadChannelCode = localStorage.getItem(
        'consumer_portal:leadChannelCode',
      );
      const phoneNumber = localStorage.getItem('consumer_portal:phoneNumber');
      const vehicleId = localStorage.getItem('consumer_portal:vehicleId');
      const error = localStorage.getItem('consumer_portal:error');

      useParameterStore.setState({
        authType,
        coApplicantCollectAgreeToTerms,
        collectAgreeToTerms,
        firstName,
        hasDuplicateApplication,
        leadChannelCode,
        phoneNumber,
        vehicleId,
        error,
      });

      return '/activation';
    },
  },

  /* sign up routes */
  ...[
    '/unified/sign-up',
    '/unified/sign-up/welcome',
    '/unified/sign-up/application-type',
    '/unified/sign-up/primary-goal',
    '/unified/sign-up/personal-details',
    '/unified/sign-up/co-applicant',
    '/unified/sign-up/vehicle-identity',
    '/unified/sign-up/select-vehicle-year',
    '/unified/sign-up/select-vehicle-make',
    '/unified/sign-up/select-vehicle-model',
    '/unified/sign-up/vehicle-confirm',
    '/unified/sign-up/review',
    '/unified/sign-up/review-co-applicant',
  ].reduce((acc, path) => {
    acc[path] = {
      targetPath: () => '/sign-up',
    };
    return acc;
  }, {}),
  '/unified/sign-up/pin-verify': {
    requiresGateContext: true,
    targetPath: ({ gateContext }) => {
      const user = get(gateContext, 'user');
      const firstName = get(user, 'firstName');
      const lastName = get(user, 'lastName');
      const primaryEmailAddress = get(user, 'primaryEmailAddress');
      const primaryPhoneNumber = get(user, 'primaryPhoneNumber');

      if (user) {
        useSignUpStore.setState({
          firstName,
          lastName,
          primaryEmailAddress,
          primaryPhoneNumber,
        });
      }

      return '/sign-up/pin-verify';
    },
  },
  '/unified/sign-up/check-user-credentials': {
    requiresGateContext: true,
    targetPath: ({ gateContext }) => {
      const primaryPhoneNumber = get(
        gateContext,
        'userVerificationError.phone',
      );
      const primaryEmailAddress = get(
        gateContext,
        'userVerificationError.email',
      );

      let userExistsException = null;

      if (primaryEmailAddress && primaryPhoneNumber) {
        userExistsException = UserExistsExceptionCode.BOTH_REGISTERED;
      }

      if (primaryEmailAddress && !primaryPhoneNumber) {
        userExistsException = UserExistsExceptionCode.EMAIL_ADDRESS_REGISTERED;
      }

      if (!primaryEmailAddress && primaryPhoneNumber) {
        userExistsException = UserExistsExceptionCode.PHONE_NUMBER_REGISTERED;
      }

      if (!primaryEmailAddress && !primaryPhoneNumber) {
        userExistsException = UserExistsExceptionCode.BOTH_NOT_FOUND;
      }

      if (userExistsException) {
        useUserStore.setState({
          userExistsException,
        });

        useSignUpStore.setState({
          primaryPhoneNumber,
          primaryEmailAddress,
        });
      }

      return '/sign-up/returning-user';
    },
  },

  /* dashboard routes */
  '/unified/dashboard': {
    targetPath: () => '/dashboard/garage',
  },
  '/unified/dashboard/vehicle/application': {
    targetPath: ({ locationState }) => {
      const currentVehicle = get(locationState, 'currentVehicle');
      const vehicleId = get(currentVehicle, 'id');

      return `/dashboard/application/${vehicleId}`;
    },
  },

  /* verification routes */
  '/unified/workflow/verification': {
    requiresGateContext: true,
    targetPath: ({ gateContext }) => {
      const vehicleId = get(gateContext, 'currentVehicle.id');

      return `/verifications/${vehicleId}`;
    },
  },
  '/unified/workflow/verification/list': {
    requiresGateContext: true,
    targetPath: ({ gateContext }) => {
      const vehicleId = get(gateContext, 'currentVehicle.id');

      return `/verifications/${vehicleId}/document-list`;
    },
  },
  '/unified/workflow/verification/upload': {
    requiresGateContext: true,
    targetPath: ({ gateContext }) => {
      const vehicleId = get(gateContext, 'currentVehicle.id');
      const selectedVerificationTask = get(
        gateContext,
        'selectedVerificationTask',
      );

      const base = `/verifications/${vehicleId}/`;

      const pathMap = {
        [DocumentEnum.DriverLicense]: 'upload-license',
        [DocumentEnum.CoApplicantDriverLicense]: 'coapp-upload-license',
        [DocumentEnum.ProofOfInsurance]: 'upload-insurance',
        [DocumentEnum.RegistrationCard]: 'upload-registration',
        [DocumentEnum.PhotoOfOdometer]: 'upload-odometer',
        [DocumentEnum.ProofOfIncome]: 'upload-income',
        [DocumentEnum.CoApplicantProofOfIncome]: 'coapp-upload-income',
        [DocumentEnum.AdditionalDocuments]: 'upload-additional',
      };

      return base + pathMap[selectedVerificationTask];
    },
  },

  /* applicant/co-applicant/offer routes */
  ...[
    '/unified/workflow/applicant',
    '/unified/workflow/applicant/personal-details',
    '/unified/workflow/applicant/housing-details',
    '/unified/workflow/applicant/previous-address',
    '/unified/workflow/applicant/primary-income',
    '/unified/workflow/applicant/primary-previous-income',
    '/unified/workflow/applicant/review-income',
    '/unified/workflow/applicant/add-additional-income',
    '/unified/workflow/applicant/review-applicant',
    '/unified/workflow/review/review-ssn',
    '/unified/workflow/co-applicant/personal-details',
    '/unified/workflow/co-applicant/housing-details',
    '/unified/workflow/co-applicant/previous-address',
    '/unified/workflow/co-applicant/primary-income',
    '/unified/workflow/co-applicant/primary-previous-income',
    '/unified/workflow/co-applicant/review-income',
    '/unified/workflow/co-applicant/add-additional-income',
    '/unified/workflow/co-applicant/review-co-applicant',
    '/unified/workflow/offers',
    '/unified/workflow/offers/gap',
    '/unified/workflow/offers/review',
  ].reduce((acc, path) => {
    acc[path] = {
      requiresGateContext: true,
      targetPath: ({ gateContext }) => {
        const vehicleId = get(gateContext, 'currentVehicle.id');

        return `/applicant/${vehicleId}`;
      },
    };
    return acc;
  }, {}),
};
